import { collection, query, where, getDocs } from 'firebase/firestore';
import { db } from '@/firebase/config';
import { organizationService } from '@/firebase/organizationService';
import { subscriptionService } from '@/firebase/subscriptionService';
import { locationService } from '@/firebase/locationService';
import { checklistService } from '@/firebase/checklistService';
import { auditLogService } from '@/services/auditLogService';
import { getInvitationsByOrganization } from '@/services/invitationService';
import { isSuperAdmin } from '@/utils/adminUtils';

export type HealthStatus = 'healthy' | 'at_risk' | 'inactive';

export interface OrganizationAnalyticsLocation {
  id: string;
  name: string;
}

export interface OrganizationAnalytics {
  organizationId: string;
  organizationName?: string;
  subscription: { plan: string; status: string };
  health: HealthStatus;
  lastActivityAt?: Date;
  locations: OrganizationAnalyticsLocation[];
  metrics: {
    totalUsers: number;
    activeUsers7d: number;
    activeUsers30d: number;
    pendingInvitations: number;
    newUsers30d: number;
    totalLocations: number;
    maxLocations?: number;
    totalAssets: number;
    totalChecklists: number;
    checklistsCompleted30d: number;
    auditActions7d: number;
    auditActions30d: number;
    logins7d: number;
    logins30d: number;
  };
}

const MS_7D = 7 * 24 * 60 * 60 * 1000;
const MS_30D = 30 * 24 * 60 * 60 * 1000;

function toDate(t: any): Date {
  if (!t) return new Date(0);
  if (t.toDate) return t.toDate();
  if (t instanceof Date) return t;
  if (typeof t === 'number') return new Date(t);
  return new Date(t);
}

function isWithin(ts: Date, windowMs: number): boolean {
  return Date.now() - ts.getTime() <= windowMs;
}

/**
 * Fetch all analytics for an organization (client-side aggregation from existing collections).
 */
export async function getOrganizationAnalytics(
  organizationId: string
): Promise<{ success: boolean; data?: OrganizationAnalytics; error?: string }> {
  try {
    const now = Date.now();
    const since7d = new Date(now - MS_7D);
    const since30d = new Date(now - MS_30D);

    // Load org name, subscription, users (needed to filter active users), and audit logs
    const usersRef = collection(db, 'users');
    const orgIdQuery = query(usersRef, where('organizationId', '==', organizationId));
    const orgIdsQuery = query(
      usersRef,
      where('organizationIds', 'array-contains', organizationId)
    );

    const [
      orgResult,
      subResult,
      locationsResult,
      checklistsResult,
      invitationsResp,
      auditResult,
      orgIdSnap,
      orgIdsSnap,
    ] = await Promise.all([
      organizationService.getOrganization(organizationId),
      subscriptionService.getSubscription(organizationId),
      locationService.getAllLocations(organizationId),
      checklistService.getAllChecklists(organizationId),
      getInvitationsByOrganization(organizationId),
      auditLogService.getLogs(organizationId, 500),
      getDocs(orgIdQuery),
      getDocs(orgIdsQuery),
    ]);

    // Build org member IDs - active users must be in this set
    const orgUserIds = new Set<string>();
    let newUsers30d = 0;
    orgIdSnap.forEach((doc) => {
      orgUserIds.add(doc.id);
      const d = doc.data();
      const created = d.createdAt ? toDate(d.createdAt) : null;
      if (created && isWithin(created, MS_30D)) newUsers30d++;
    });
    orgIdsSnap.forEach((doc) => {
      if (orgUserIds.has(doc.id)) return;
      orgUserIds.add(doc.id);
      const d = doc.data();
      const created = d.createdAt ? toDate(d.createdAt) : null;
      if (created && isWithin(created, MS_30D)) newUsers30d++;
    });
    const totalUsers = orgUserIds.size;

    const organizationName =
      orgResult.success && orgResult.data?.name ? orgResult.data.name : undefined;

    const subscription = subResult.success && subResult.data
      ? { plan: String(subResult.data.plan || '—'), status: subResult.data.status }
      : { plan: '—', status: '—' };

    const locations: OrganizationAnalyticsLocation[] = locationsResult.success && locationsResult.data
      ? locationsResult.data.map((loc) => ({ id: loc.id || '', name: loc.name || 'Unnamed' }))
      : [];
    const totalLocations = locations.length;

    const totalChecklists = checklistsResult.success && checklistsResult.data
      ? checklistsResult.data.length
      : 0;

    const pendingInvitations =
      invitationsResp.success && invitationsResp.invitations
        ? invitationsResp.invitations.length
        : 0;

    // Max locations from subscription - only when we have an explicit licensed quantity
    let maxLocations: number | undefined;
    if (subResult.success && subResult.data) {
      const sub = subResult.data;
      const quantity = sub.quantity ?? 0;
      const canAdd = await subscriptionService.canAddLocation(
        organizationId,
        totalLocations
      );
      // Show limit only for paid plans with quantity; communicate plan defaults to 10 which may not reflect actual agreement
      const isPaidPlan = sub.plan && !['communicate', null].includes(sub.plan);
      maxLocations = isPaidPlan && quantity > 0 ? (canAdd.maxLocations ?? quantity) : undefined;
    }

    // Audit logs: last activity, counts, active users, logins
    // Exclude super admin actions (e.g. preview mode) - they shouldn't count as org activity
    const logs = auditResult.success && auditResult.data ? auditResult.data : [];
    const activeUserIds7d = new Set<string>();
    const activeUserIds30d = new Set<string>();
    let logins7d = 0;
    let logins30d = 0;
    let auditActions7d = 0;
    let auditActions30d = 0;
    let lastActivityAt: Date | undefined;

    const isSuperAdminAction = (log: any) => {
      const email = log.performedByEmail || log.userEmail;
      if (email && isSuperAdmin(email)) return true;
      if (log.action === 'preview_mode_entered' || log.action === 'preview_mode_exited') return true;
      return false;
    };

    for (const log of logs) {
      if (isSuperAdminAction(log)) continue;

      const ts = toDate(log.timestamp);
      if (!lastActivityAt || ts > lastActivityAt) lastActivityAt = ts;

      if (isWithin(ts, MS_7D)) {
        auditActions7d++;
        if (log.action === 'user_login') logins7d++;
        const uid = log.performedBy || (log as any).userId;
        if (uid && orgUserIds.has(uid)) {
          activeUserIds7d.add(uid);
        }
      }
      if (isWithin(ts, MS_30D)) {
        auditActions30d++;
        if (log.action === 'user_login') logins30d++;
        const uid = log.performedBy || (log as any).userId;
        if (uid && orgUserIds.has(uid)) {
          activeUserIds30d.add(uid);
        }
      }
    }

    // Assets count
    const assetsRef = collection(db, 'assets');
    const assetsQuery = query(assetsRef, where('organizationId', '==', organizationId));
    const assetsSnap = await getDocs(assetsQuery);
    const totalAssets = assetsSnap.size;

    // Checklist runs: completed in last 30d
    const runsRef = collection(db, 'checklist_runs');
    const runsQuery = query(
      runsRef,
      where('organizationId', '==', organizationId),
      where('status', '==', 'completed')
    );
    let runsSnap;
    try {
      runsSnap = await getDocs(runsQuery);
    } catch {
      runsSnap = { size: 0, forEach: () => {} };
    }
    let checklistsCompleted30d = 0;
    runsSnap.forEach((doc) => {
      const d = doc.data();
      const completedAt = d.completedAt ? toDate(d.completedAt) : null;
      if (completedAt && isWithin(completedAt, MS_30D)) checklistsCompleted30d++;
    });

    // Health
    const isActiveSubscription =
      subResult.success &&
      subResult.data &&
      ['active', 'trialing'].includes(subResult.data.status);
    let health: HealthStatus = 'inactive';
    if (isActiveSubscription) {
      if (lastActivityAt && isWithin(lastActivityAt, MS_7D)) {
        health = 'healthy';
      } else if (lastActivityAt && isWithin(lastActivityAt, MS_30D)) {
        health = 'at_risk';
      } else {
        health = 'at_risk';
      }
    }

    const data: OrganizationAnalytics = {
      organizationId,
      organizationName,
      subscription,
      health,
      lastActivityAt,
      locations,
      metrics: {
        totalUsers,
        activeUsers7d: activeUserIds7d.size,
        activeUsers30d: activeUserIds30d.size,
        pendingInvitations,
        newUsers30d,
        totalLocations,
        maxLocations,
        totalAssets,
        totalChecklists,
        checklistsCompleted30d,
        auditActions7d,
        auditActions30d,
        logins7d,
        logins30d,
      },
    };

    return { success: true, data };
  } catch (error: any) {
    console.error('Error getting organization analytics:', error);
    return { success: false, error: error.message };
  }
}
